import React, { useEffect, useState } from "react";
import { scheduleService } from "../../firebase/services";
import {
  Calendar,
  Clock,
  Plus,
  Trash2,
  Edit2,
  Loader2,
  AlertCircle,
  User,
} from "lucide-react";

export const AdminSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState({
    id: null,
    dayNumber: 1,
    date: new Date().toISOString().split("T")[0],
    topic: "",
    instructor: "Cyber Security Specialist",
    startTime: "05:00 PM",
    endTime: "07:00 PM",
    description: "",
  });

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const list = await scheduleService.getSchedules();
      setSchedules(list);
    } catch (err) {
      console.error("Fetch schedules error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const handleOpenAdd = () => {
    const nextDay = schedules.length + 1;
    setForm({
      id: null,
      dayNumber: Math.min(nextDay, 15),
      date: new Date().toISOString().split("T")[0],
      topic: "",
      instructor: "Kian Tech Specialist",
      startTime: "05:00 PM",
      endTime: "07:00 PM",
      description: "",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setForm({
      id: item.id,
      dayNumber: item.dayNumber,
      date: item.date,
      topic: item.topic,
      instructor: item.instructor || "",
      startTime: item.startTime,
      endTime: item.endTime,
      description: item.description || "",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) return alert("Please specify topic.");

    setSaving(true);
    try {
      await scheduleService.saveScheduleDay(form);
      setShowModal(false);
      fetchSchedules();
    } catch (err) {
      console.error("Save schedule error:", err);
      alert(err.message || "Failed to save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this schedule entry?")) return;
    try {
      await scheduleService.deleteScheduleDay(id);
      fetchSchedules();
    } catch (err) {
      console.error("Delete schedule error:", err);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">15-Day Masterclass Class Schedule</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Manage daily topics, instructors, and session timings for student view.
          </p>
        </div>
        <button
          onClick={handleOpenAdd}
          data-testid="admin-add-schedule-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-semibold shadow-md transition-all self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Add Schedule Day</span>
        </button>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            <span className="text-xs font-mono">Loading schedules from Firestore...</span>
          </div>
        ) : schedules.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground space-y-2">
            <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="text-sm font-semibold text-foreground">No schedule entries yet.</div>
            <p className="text-xs max-w-sm mx-auto font-mono">
              Click "Add Schedule Day" to create the 15-day syllabus calendar.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {schedules.map((item) => (
              <div
                key={item.id}
                className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-start space-x-4">
                  <div className="px-3 py-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 font-mono text-center border border-cyan-500/20 flex-shrink-0">
                    <div className="text-[9px] uppercase font-semibold">Day</div>
                    <div className="text-base font-bold">{item.dayNumber}</div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                      <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                      <span>{item.date}</span>
                      <span>•</span>
                      <Clock className="w-3.5 h-3.5 text-cyan-400" />
                      <span>
                        {item.startTime} - {item.endTime}
                      </span>
                    </div>
                    <h3 className="text-sm font-bold text-foreground font-sans">{item.topic}</h3>
                    {item.instructor && (
                      <div className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                        <User className="w-3 h-3 text-emerald-400" />
                        <span>Instructor: {item.instructor}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleOpenEdit(item)}
                    data-testid={`edit-schedule-${item.id}`}
                    className="p-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground border border-border text-xs transition-colors"
                    title="Edit Schedule"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    data-testid={`delete-schedule-${item.id}`}
                    className="p-2 rounded-lg bg-rose-600/10 hover:bg-rose-600/20 text-rose-400 border border-rose-500/20 transition-colors"
                    title="Delete Entry"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-card border border-border max-w-lg w-full rounded-2xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="font-bold text-base">
                {form.id ? "Edit Schedule Day" : "Add Schedule Day"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-muted-foreground hover:text-foreground text-xs font-mono"
              >
                ✕ Close
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs font-mono">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium text-foreground mb-1">Day Number (1-15)</label>
                  <input
                    type="number"
                    min="1"
                    max="15"
                    required
                    value={form.dayNumber}
                    onChange={(e) => setForm({ ...form, dayNumber: Number(e.target.value) })}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500 font-bold text-emerald-400"
                  />
                </div>

                <div>
                  <label className="block font-medium text-foreground mb-1">Date</label>
                  <input
                    type="date"
                    required
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block font-medium text-foreground mb-1">Session Topic *</label>
                <input
                  type="text"
                  required
                  data-testid="admin-sched-topic-input"
                  placeholder="e.g. Linux Command Line Mastery & Security Administration"
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium text-foreground mb-1">Start Time</label>
                  <input
                    type="text"
                    placeholder="05:00 PM"
                    value={form.startTime}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block font-medium text-foreground mb-1">End Time</label>
                  <input
                    type="text"
                    placeholder="07:00 PM"
                    value={form.endTime}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block font-medium text-foreground mb-1">Instructor Name</label>
                <input
                  type="text"
                  placeholder="e.g. Kian Cyber Security Faculty"
                  value={form.instructor}
                  onChange={(e) => setForm({ ...form, instructor: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block font-medium text-foreground mb-1">Description / Notes</label>
                <textarea
                  rows={2}
                  placeholder="Prerequisites, tools to install (e.g. Kali Linux, Wireshark)..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="admin-sched-submit-btn"
                  className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  <span>Save Day</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
